import { apiFetch } from './api';

// ─── TYPES ──────────────────────────────────────────────────────────────────

export type HomeRole = 'owner' | 'member' | 'viewer';

export type ServiceType =
  | 'maintenance'
  | 'warranty'
  | 'bookings'
  | 'issues'
  | 'orders'
  | 'protection_plans';

export const ALL_SERVICE_TYPES: ServiceType[] = [
  'maintenance',
  'warranty',
  'bookings',
  'issues',
  'orders',
  'protection_plans',
];

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  maintenance: 'Maintenance',
  warranty: 'Warranty',
  bookings: 'Bookings',
  issues: 'Issues',
  orders: 'Orders',
  protection_plans: 'Protection Plans',
};

export interface FamilyMemberUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface FamilyMemberAssetAccess {
  assetId: string;
  asset?: {
    id: string;
    name: string;
    category: string;
    imageUrl?: string;
  };
}

export interface FamilyMemberServiceAccess {
  serviceType: ServiceType;
}

export interface FamilyMember {
  id: string;
  userId: string;
  homeId: string;
  role: HomeRole;
  relation?: string;
  phone?: string;
  grantFutureAssets: boolean;
  joinedAt: string;
  user: FamilyMemberUser;
  assetAccess: FamilyMemberAssetAccess[];
  serviceAccess: FamilyMemberServiceAccess[];
  home?: {
    id: string;
    name?: string;
    address: string;
    city: string;
    state: string;
    imageUrl?: string | null;
    owners?: Array<{ user: { name: string; email: string } }>;
  };
}

export interface FamilyInvite {
  id: string;
  homeId: string;
  email: string;
  role: HomeRole;
  relation?: string;
  inviteeName?: string;
  grantFutureAssets: boolean;
  assetIds?: string[];
  serviceTypes?: ServiceType[];
  status: 'pending' | 'accepted' | 'declined';
  token: string;
  expiresAt: string;
  createdAt: string;
  home?: {
    id: string;
    name?: string;
    address: string;
    city: string;
  };
  invitedBy?: FamilyMemberUser;
}

export interface InviteFamilyMemberRequest {
  homeId: string;
  email: string;
  role: HomeRole;
  relation?: string;
  inviteeName?: string;
  grantFutureAssets?: boolean;
  assetIds?: string[];
  serviceTypes?: ServiceType[];
}

export interface UpdateMemberAccessRequest {
  role?: HomeRole;
  relation?: string;
  grantFutureAssets?: boolean;
  assetIds?: string[];
  serviceTypes?: ServiceType[];
}

export interface ValidatedInvite {
  id: string;
  email: string;
  role: HomeRole;
  relation?: string;
  inviteeName?: string;
  home: { id: string; name?: string; address: string; city: string; state: string };
  invitedBy: { name: string; email: string };
  expiresAt: string;
}

// ─── OWNER: MEMBER MANAGEMENT ────────────────────────────────────────────────

export async function getFamilyMembers(homeId: string): Promise<FamilyMember[]> {
  const response = await apiFetch<{ success: boolean; data: FamilyMember[] }>(
    `/v1/family?homeId=${homeId}`
  );
  return response.data ?? [];
}

export async function inviteFamilyMember(request: InviteFamilyMemberRequest): Promise<void> {
  await apiFetch<{ success: boolean; data: null }>('/v1/family/invite', {
    method: 'POST',
    body: request,
  });
}

export async function removeFamilyMember(memberId: string): Promise<void> {
  await apiFetch(`/v1/family/${memberId}`, { method: 'DELETE' });
}

export async function getMemberAccess(memberId: string): Promise<FamilyMember> {
  const response = await apiFetch<{ success: boolean; data: FamilyMember }>(
    `/v1/family/${memberId}/access`
  );
  return response.data;
}

export async function updateMemberAccess(
  memberId: string,
  payload: UpdateMemberAccessRequest
): Promise<FamilyMember> {
  const response = await apiFetch<{ success: boolean; data: FamilyMember }>(
    `/v1/family/${memberId}/access`,
    { method: 'PUT', body: payload }
  );
  return response.data;
}

// ─── OWNER: INVITE MANAGEMENT ────────────────────────────────────────────────

export async function getPendingInvites(homeId: string): Promise<FamilyInvite[]> {
  const response = await apiFetch<{ success: boolean; data: FamilyInvite[] }>(
    `/v1/family/pending-invites?homeId=${homeId}`
  );
  return response.data ?? [];
}

export async function cancelInvite(inviteId: string): Promise<void> {
  await apiFetch(`/v1/family/invites/${inviteId}`, { method: 'DELETE' });
}

export async function resendInvite(inviteId: string): Promise<void> {
  await apiFetch('/v1/family/resend-invite', {
    method: 'POST',
    body: { inviteId },
  });
}

// ─── INVITEE: ACCEPT / DECLINE FLOW ─────────────────────────────────────────

export async function validateInvite(token: string): Promise<ValidatedInvite> {
  const response = await apiFetch<{ success: boolean; data: ValidatedInvite }>(
    `/v1/family/validate-invite?token=${encodeURIComponent(token)}`
  );
  return response.data;
}

export async function acceptInvite(token: string): Promise<void> {
  await apiFetch('/v1/family/accept-invite', {
    method: 'POST',
    body: { token },
  });
}

export async function declineInvite(token: string): Promise<void> {
  await apiFetch('/v1/family/decline-invite', {
    method: 'POST',
    body: { token },
  });
}

// ─── MEMBER: OWN MEMBERSHIPS & INVITES ───────────────────────────────────────

export async function getMyAccess(): Promise<FamilyMember[]> {
  const response = await apiFetch<{ success: boolean; data: FamilyMember[] }>(
    '/v1/family/my-access'
  );
  return response.data ?? [];
}

export async function getMyInvites(): Promise<FamilyInvite[]> {
  const response = await apiFetch<{ success: boolean; data: FamilyInvite[] }>(
    '/v1/family/my-invites'
  );
  return response.data ?? [];
}

export async function leaveHome(memberId: string): Promise<void> {
  await apiFetch(`/v1/family/leave/${memberId}`, { method: 'DELETE' });
}
